import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  seancesApi, TYPE_LABEL, STATUS_LABEL, VOTE_LABEL,
} from '../services/seancesApi';
import './Seances.css';

const STATUS_COLOR = {
  annoncee: '#E8B53C',
  tenue:    '#1B6985',
  publiee:  '#34A853',
};

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
}
function fmtTime(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

const SeanceDetail = () => {
  const { id } = useParams();
  const [seance, setSeance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    seancesApi.get(id)
      .then((data) => { if (!cancelled) { setSeance(data.seance); setLoading(false); } })
      .catch((err) => { if (!cancelled) { setError(err.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [id]);

  if (loading) {
    return (
      <main className="seance-detail">
        <div className="wrap" style={{ padding: 80 }}>
          <p className="sea-muted">Chargement de la séance…</p>
        </div>
      </main>
    );
  }
  if (error || !seance) {
    return (
      <main className="seance-detail">
        <div className="wrap" style={{ padding: 80 }}>
          <p className="sea-muted" style={{ marginBottom: 16 }}>Séance introuvable.</p>
          <Link to="/seances" className="sd-back">← Retour aux séances</Link>
        </div>
      </main>
    );
  }

  const present = seance.participants?.filter((p) => p.present) || [];
  const absent  = seance.participants?.filter((p) => !p.present) || [];

  return (
    <main className="seance-detail">
      {/* ============ HERO ============ */}
      <header className="sd-hero">
        <div className="wrap">
          <Link to="/seances" className="sd-back">← Toutes les séances</Link>
          <h1 className="sd-title">{seance.title}</h1>
          {seance.summary && <p className="sd-summary">{seance.summary}</p>}
          <div className="sd-meta-row">
            <span className="ref">{seance.reference || '—'}</span>
            <span>·</span>
            <span>{TYPE_LABEL[seance.type] || seance.type}</span>
            <span>·</span>
            <span>{fmtDate(seance.sessionDate)} · {fmtTime(seance.sessionDate)}</span>
            {seance.location && (<><span>·</span><span>📍 {seance.location}</span></>)}
            <span className="stat" style={{ background: STATUS_COLOR[seance.status] || '#888' }}>
              {STATUS_LABEL[seance.status] || seance.status}
            </span>
          </div>
        </div>
      </header>

      {/* ============ ORDRE DU JOUR ============ */}
      <section className="sd-section">
        <div className="wrap">
          <h2>Ordre du jour<span className="ital"> · {seance.agenda?.length || 0}</span></h2>
          {seance.agenda?.length > 0 ? (
            <ul className="sd-list">
              {seance.agenda.map((a, i) => (
                <li key={i}>
                  <div style={{ flex: 1 }}>
                    <div className="titre">{i + 1}. {a.titre}</div>
                    {a.rapporteur && <span className="rap">Rapporteur · {a.rapporteur}</span>}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="sd-empty">Aucun point inscrit.</div>
          )}
        </div>
      </section>

      {/* ============ PARTICIPANTS (si séance tenue) ============ */}
      {seance.participants?.length > 0 && (
        <section className="sd-section">
          <div className="wrap">
            <h2>Participants<span className="ital"> · {present.length} présent{present.length > 1 ? 's' : ''}{absent.length > 0 ? `, ${absent.length} absent${absent.length > 1 ? 's' : ''}` : ''}</span></h2>
            <div className="sd-participants">
              {seance.participants.map((p, i) => (
                <div key={i} className={`sd-participant ${p.present ? '' : 'absent'}`}>
                  <div>
                    <b>{p.name}</b>
                    {p.role && <small>{p.role}</small>}
                  </div>
                  <span className="badge">{p.present ? 'Présent' : 'Absent'}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ============ DÉCISIONS (uniquement si publiée) ============ */}
      {seance.status === 'publiee' && (
        <section className="sd-section">
          <div className="wrap">
            <h2>Délibérations<span className="ital"> · {seance.decisions?.length || 0}</span></h2>
            {seance.decisions?.length > 0 ? (
              <ul className="sd-list">
                {seance.decisions.map((d, i) => (
                  <li key={i}>
                    <div style={{ flex: 1 }}>
                      <div className="titre">{d.libelle}</div>
                      {d.tally && <span className="tally">Vote : {d.tally}</span>}
                    </div>
                    <span className={`vote ${d.vote}`}>{VOTE_LABEL[d.vote] || d.vote}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="sd-empty">Aucune décision enregistrée.</div>
            )}
          </div>
        </section>
      )}

      {/* ============ PV OFFICIEL ============ */}
      {seance.status === 'publiee' && seance.pvUrl && (
        <section className="sd-section">
          <div className="wrap">
            <div className="sd-pv-card">
              <div>
                <h3>Procès-verbal officiel</h3>
                <p>Document signé, mis en ligne le {fmtDate(seance.publishedAt)}.</p>
              </div>
              <a href={seance.pvUrl} target="_blank" rel="noopener noreferrer">
                Télécharger le PV <span>↗</span>
              </a>
            </div>
          </div>
        </section>
      )}

      {/* ============ AVERTISSEMENT statut annoncée ============ */}
      {seance.status === 'annoncee' && (
        <section className="sd-section">
          <div className="wrap">
            <div className="sd-empty">
              Séance pas encore tenue. Le procès-verbal sera publié après la séance.
            </div>
          </div>
        </section>
      )}
      {seance.status === 'tenue' && (
        <section className="sd-section">
          <div className="wrap">
            <div className="sd-empty">
              Séance tenue. Le procès-verbal officiel sera publié prochainement.
            </div>
          </div>
        </section>
      )}
    </main>
  );
};

export default SeanceDetail;
